import { tagged } from "@mirage/service-logging";
import Sentry, { isSentryInitialized } from "@mirage/shared/sentry";
import pqueue from "p-queue";

const logger = tagged("storage/storage-pqueue");

type Task<T> = (() => Promise<T>) | (() => T);

/**
 * A simple serial queue used by storage adapters. Mostly just a wrapper around pqueue with
 * diagnostics logging to ensure that we don't have too many operations queued up.
 */
export class StoragePQueue {
  queue: pqueue = new pqueue({ concurrency: 1 });

  add<T>(task: Task<T>): Promise<void | T> {
    this.checkQueueLength();
    return this.queue.add(task);
  }

  /**
   * Check the queue length and log and exception if it's too high. This does NOT actually block
   * the queue, it currently just logs an error to help with diagnostics.
   */
  private checkQueueLength() {
    if (this.queue.size >= QUEUE_LENGTH_WARNING_THRESHOLD) {
      // capture exception to log stack trace
      const errorMessage = `[storage/storage-pqueue] high queue length: ${this.queue.size}`;
      if (isSentryInitialized()) {
        Sentry.captureException(new Error(errorMessage));
      }
      // storage may be used before Sentry is initialized, so we want to also log to console
      logger.error(errorMessage);
    }
  }
}

// queue length threshold above which we log an error
const QUEUE_LENGTH_WARNING_THRESHOLD = 100;
